import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api-client";
import { User, Loader2, X } from "lucide-react";
import type { Contact } from "@/api/generated/client/models";

interface SearchResult { id: number; name: string; contactType?: string }

/**
 * ContactsPicker for create dialogs — manages a list of selected contact IDs.
 */
export function CreateContactsPicker({ selectedIds, onChange }: { selectedIds: number[]; onChange: (ids: number[]) => void }) {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const labelCache = useRef<Map<number, string>>(new Map());

    useEffect(() => {
        if (!search) { setResults([]); setIsSearching(false); return; }
        setIsSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiClient.contacts.listContacts({ search, limit: 15 });
                const contacts = (res.contacts || [])
                    .filter((c: Contact) => !selectedIds.includes(c.id))
                    .map((c: Contact) => ({ id: c.id, name: `${c.firstName} ${c.lastName}`, contactType: c.contactType }));
                setResults(contacts);
            } catch { setResults([]); }
            setIsSearching(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, selectedIds]);

    const selectedLabels = selectedIds.map(id => ({ id, label: labelCache.current.get(id) || `Contact #${id}` }));

    return (
        <div className="space-y-3">
            <Label className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />Link Contacts</Label>
            {selectedLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedLabels.map(({ id, label }) => (
                        <Badge key={id} variant="secondary" className="gap-1 pr-1">
                            {label}
                            <button type="button" aria-label={`Remove ${label}`} onClick={() => onChange(selectedIds.filter(sid => sid !== id))} className="ml-0.5 rounded-full hover:bg-muted p-0.5">
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
            <div>
                <div className="relative">
                    <Input placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-sm pr-8" />
                    {isSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground animate-spin" />}
                </div>
                {!isSearching && search && results.length > 0 && (
                    <div className="border rounded-md max-h-32 overflow-y-auto overflow-x-hidden mt-1">
                        {results.slice(0, 8).map((c) => (
                            <button key={c.id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2 min-w-0" onClick={() => { labelCache.current.set(c.id, c.name); onChange([...selectedIds, c.id]); setSearch(""); }}>
                                <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span className="truncate">{c.name}</span>
                                {c.contactType && <span className="text-muted-foreground text-xs ml-auto shrink-0">{c.contactType}</span>}
                            </button>
                        ))}
                    </div>
                )}
                {!isSearching && search && results.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">No matching contacts</p>
                )}
            </div>
        </div>
    );
}
